import { createFileRoute, redirect } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { glass } from "./portal";
import { useIsAdmin } from "@/lib/portal/useIsAdmin";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { TrendingUp, DollarSign, AlertCircle, CheckCircle2 } from "lucide-react";

export const Route = createFileRoute("/_authenticated/portal/admin/billing")({
  component: AdminBilling,
});

function AdminBilling() {
  const { data: isAdmin, isLoading } = useIsAdmin();

  const { data } = useQuery({
    queryKey: ["admin", "billing"],
    enabled: !!isAdmin,
    queryFn: async () => {
      const [invoices, subs, projects] = await Promise.all([
        supabase.from("invoices").select("*").order("issued_at", { ascending: false }),
        supabase.from("subscriptions").select("*").order("started_at", { ascending: false }),
        supabase.from("projects").select("id, name"),
      ]);
      return {
        invoices: invoices.data ?? [],
        subs: subs.data ?? [],
        projects: Object.fromEntries((projects.data ?? []).map((p) => [p.id, p.name])),
      };
    },
  });

  if (isLoading) return <div className="text-white/60 text-sm">Verifica accesso…</div>;
  if (!isAdmin) throw redirect({ to: "/portal" });

  const activeSubs = (data?.subs ?? []).filter((s) => s.status === "active");
  const mrr = activeSubs.reduce((sum, s) => sum + Number(s.price ?? 0), 0);
  const paid = (data?.invoices ?? []).filter((i) => i.status === "paid").reduce((sum, i) => sum + Number(i.amount ?? 0), 0);
  const pending = (data?.invoices ?? []).filter((i) => i.status === "pending" || i.status === "overdue").reduce((sum, i) => sum + Number(i.amount ?? 0), 0);

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-fuchsia-300">Admin</div>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Fatture & Abbonamenti</h1>
        <p className="text-white/60 mt-1">MRR, incassi e stato pagamenti di tutti i clienti.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi icon={TrendingUp} label="MRR" value={`CHF ${mrr.toLocaleString("it-CH")}`} tone="fuchsia" />
        <Kpi icon={CheckCircle2} label="Abbonamenti attivi" value={String(activeSubs.length)} tone="emerald" />
        <Kpi icon={DollarSign} label="Incassato" value={`CHF ${paid.toLocaleString("it-CH")}`} tone="cyan" />
        <Kpi icon={AlertCircle} label="Da incassare" value={`CHF ${pending.toLocaleString("it-CH")}`} tone={pending > 0 ? "amber" : "emerald"} />
      </div>

      <section>
        <h2 className="font-semibold mb-3">Abbonamenti</h2>
        <div className={`${glass} overflow-hidden`}>
          <div className="grid grid-cols-[1fr_100px_120px_100px_140px] px-4 py-3 text-[10px] uppercase tracking-widest text-white/40 border-b border-white/5">
            <div>Cliente</div><div>Piano</div><div>Prezzo</div><div>Status</div><div>Rinnovo</div>
          </div>
          {(data?.subs ?? []).map((s) => (
            <div key={s.id} className="grid grid-cols-[1fr_100px_120px_100px_140px] items-center px-4 py-3 text-sm border-b border-white/5 last:border-b-0">
              <div className="font-medium truncate">{data?.projects[s.project_id] ?? "—"}</div>
              <div className="text-white/70 text-xs uppercase">{s.plan}</div>
              <div className="font-semibold">{s.currency} {Number(s.price).toLocaleString("it-CH")}<span className="text-white/40 text-xs">/{s.billing_cycle}</span></div>
              <div><span className={`px-2 py-0.5 rounded-full text-[10px] uppercase ${s.status === "active" ? "bg-emerald-500/20 text-emerald-300" : s.status === "cancelled" ? "bg-red-500/20 text-red-300" : "bg-amber-500/20 text-amber-300"}`}>{s.status}</span></div>
              <div className="text-xs text-white/60">{s.renewal_date ? format(new Date(s.renewal_date), "d MMM yyyy", { locale: it }) : "—"}</div>
            </div>
          ))}
          {(data?.subs ?? []).length === 0 && <div className="p-8 text-center text-sm text-white/40">Nessun abbonamento</div>}
        </div>
      </section>

      <section>
        <h2 className="font-semibold mb-3">Fatture</h2>
        <div className={`${glass} overflow-hidden`}>
          <div className="grid grid-cols-[110px_1fr_120px_120px_100px_100px] px-4 py-3 text-[10px] uppercase tracking-widest text-white/40 border-b border-white/5">
            <div>Numero</div><div>Cliente</div><div>Importo</div><div>Scadenza</div><div>Status</div><div>Data</div>
          </div>
          {(data?.invoices ?? []).map((i) => (
            <div key={i.id} className="grid grid-cols-[110px_1fr_120px_120px_100px_100px] items-center px-4 py-3 text-sm border-b border-white/5 last:border-b-0">
              <div className="font-mono text-xs text-white/60">{i.invoice_number}</div>
              <div className="font-medium truncate">{data?.projects[i.project_id] ?? "—"}</div>
              <div className="font-semibold">{i.currency} {Number(i.amount).toLocaleString("it-CH")}</div>
              <div className="text-xs text-white/60">{i.due_date ? format(new Date(i.due_date), "d MMM yyyy", { locale: it }) : "—"}</div>
              <div><span className={`px-2 py-0.5 rounded-full text-[10px] uppercase ${i.status === "paid" ? "bg-emerald-500/20 text-emerald-300" : i.status === "overdue" ? "bg-red-500/20 text-red-300" : "bg-amber-500/20 text-amber-300"}`}>{i.status}</span></div>
              <div className="text-xs text-white/50">{i.issued_at ? format(new Date(i.issued_at), "d MMM", { locale: it }) : "—"}</div>
            </div>
          ))}
          {(data?.invoices ?? []).length === 0 && <div className="p-8 text-center text-sm text-white/40">Nessuna fattura</div>}
        </div>
      </section>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tone }: { icon: typeof TrendingUp; label: string; value: string; tone: "fuchsia" | "cyan" | "emerald" | "amber" }) {
  const tones = {
    fuchsia: "from-fuchsia-500/20 to-fuchsia-500/5 text-fuchsia-300 ring-fuchsia-500/30",
    cyan: "from-cyan-500/20 to-cyan-500/5 text-cyan-300 ring-cyan-500/30",
    emerald: "from-emerald-500/20 to-emerald-500/5 text-emerald-300 ring-emerald-500/30",
    amber: "from-amber-500/20 to-amber-500/5 text-amber-300 ring-amber-500/30",
  };
  return (
    <div className={`${glass} p-5`}>
      <div className={`inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ${tones[tone]} ring-1`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="mt-4 text-[10px] uppercase tracking-widest text-white/50">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
    </div>
  );
}
